import React, { useState } from 'react';
import { ArrowLeftIcon } from 'lucide-react';
import { DS } from '../ds';
import { BlogContextProvider } from '../../contexts/BlogContext';
import { ArticleList } from './blog/ArticleList';
import { BlogQA } from './blog/BlogQA';
import type { BlogArticle } from '../../data/blogArticles';
interface BlogPageProps {
  onBack: () => void;
}
type Tab = 'articles' | 'qa';
function truncate(text: string, max: number) {
  return text.length > max ? `${text.slice(0, max)}…` : text;
}
function BlogPageContent({
  onBack
}: BlogPageProps) {
  const [activeTab, setActiveTab] = useState<Tab>('articles');
  const [selectedArticle, setSelectedArticle] = useState<BlogArticle | null>(null);
  const goHome = () => {
    setSelectedArticle(null);
    onBack();
  };
  const goToArticles = () => {
    setSelectedArticle(null);
    setActiveTab('articles');
    window.scrollTo({
      top: 0,
      behavior: 'auto'
    });
  };
  return <section id="blog" style={{
    background: '#fff',
    minHeight: '100vh',
    paddingTop: '68px'
  }} data-id="element-323" data-mp="3tl4p0h1vuyxj">
      {/* Breadcrumb */}
      <nav aria-label="Ruta de navegación" style={{
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '16px 24px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      flexWrap: 'wrap',
      borderBottom: `1px solid ${DS.color.border}`,
      fontFamily: DS.font.body,
      fontSize: '13px'
    }} data-id="element-324" data-mp="11p0s6c59dyfy">
        <button type="button" onClick={goHome} style={{
        background: 'transparent',
        border: 'none',
        padding: 0,
        color: DS.color.teal,
        fontWeight: 700,
        fontSize: '13px',
        cursor: 'pointer',
        fontFamily: DS.font.body
      }} data-id="element-325" data-mp="nr2hphu26m5q">
          Alinova
        </button>
        <span aria-hidden="true" style={{
        color: DS.color.border
      }} data-id="element-326" data-mp="286xdvivz9yzw">
          /
        </span>
        {selectedArticle ? <>
            <button type="button" onClick={goToArticles} style={{
          background: 'transparent',
          border: 'none',
          padding: 0,
          color: DS.color.teal,
          fontWeight: 700,
          fontSize: '13px',
          cursor: 'pointer',
          fontFamily: DS.font.body
        }} data-id="element-327" data-mp="83mt7rvxvbzr">
              Blog
            </button>
            <span aria-hidden="true" style={{
          color: DS.color.border
        }} data-id="element-328" data-mp="3gkhho2bzotub">
              /
            </span>
            <span style={{
          color: DS.color.muted
        }} data-id="element-329" data-mp="3v941ag8ng7rc">
              {truncate(selectedArticle.title, 50)}
            </span>
          </> : <span style={{
        color: DS.color.muted
      }} data-id="element-330" data-mp="3qh2ngpvu28z7">Blog</span>}
      </nav>

      {/* Hero */}
      {!selectedArticle && <div style={{
      position: 'relative',
      padding: '56px 24px',
      background: 'linear-gradient(135deg, rgba(0,170,212,0.08) 0%, rgba(45,212,212,0.05) 100%)',
      borderBottom: `1px solid ${DS.color.border}`,
      overflow: 'hidden'
    }} data-id="element-331" data-mp="2xtzv6fhr2uhe">
          <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        position: 'relative'
      }} data-id="element-332" data-mp="14c2a7zzm5wm4">
            <button type="button" onClick={onBack} style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          background: 'transparent',
          border: 'none',
          padding: 0,
          marginBottom: '24px',
          color: DS.color.teal,
          fontFamily: DS.font.body,
          fontSize: '14px',
          fontWeight: 700,
          cursor: 'pointer'
        }} data-id="element-333" data-mp="1w2fewbltsr8k">
              <ArrowLeftIcon size={16} aria-hidden="true" data-id="element-334" data-mp="gn9kwmdhyn0h" />
              Volver al inicio
            </button>

            <h1 style={{
          fontFamily: DS.font.heading,
          fontSize: 'clamp(2.2rem, 5vw, 3rem)',
          fontWeight: 700,
          color: DS.color.ink,
          letterSpacing: '-0.03em',
          lineHeight: 1.1,
          margin: '0 0 14px'
        }} data-id="element-335" data-mp="vwmtwt9v7ckh">
              Alinova Blog
            </h1>
            <p style={{
          fontFamily: DS.font.body,
          fontSize: '17px',
          color: DS.color.body,
          maxWidth: '520px',
          margin: 0
        }} data-id="element-336" data-mp="1etegpcom89ta">
              Conocimiento clínico, tendencias y mejores prácticas en
              ortodoncia invisible.
            </p>
          </div>
        </div>}

      {/* Tabs */}
      {!selectedArticle && <div style={{
      padding: '0 24px',
      borderBottom: `1px solid ${DS.color.border}`
    }} data-id="element-337" data-mp="3lk446aaf92m4">
          <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        display: 'flex',
        gap: '28px'
      }} data-id="element-338" data-mp="214fu4lrgu04r">
            {[{
          id: 'articles' as const,
          label: 'Artículos'
        }, {
          id: 'qa' as const,
          label: 'Preguntas & Respuestas'
        }].map(tab => {
          const isActive = activeTab === tab.id;
          return <button key={tab.id} type="button" onClick={() => setActiveTab(tab.id)} aria-current={isActive ? 'page' : undefined} style={{
            padding: '18px 0',
            background: 'transparent',
            border: 'none',
            color: isActive ? DS.color.teal : DS.color.body,
            fontFamily: DS.font.body,
            fontSize: '14px',
            fontWeight: 700,
            cursor: 'pointer',
            borderBottom: isActive ? `3px solid ${DS.color.aqua}` : '3px solid transparent',
            transition: `color 0.25s ${DS.ease.smooth}, border-color 0.25s ${DS.ease.smooth}`
          }} data-id="element-339" data-mp="2oqw2npjk95ex">
                  {tab.label}
                </button>;
        })}
          </div>
        </div>}

      {/* Contenido */}
      <div style={{
      padding: '56px 24px 80px'
    }} data-id="element-340" data-mp="2kp00d3fuzmol">
        <div style={{
        maxWidth: '1200px',
        margin: '0 auto'
      }} data-id="element-341" data-mp="2zexjv62ufo7g">
          {activeTab === 'articles' ? <ArticleList selectedArticle={selectedArticle} onSelectArticle={setSelectedArticle} onHome={goHome} data-id="element-342" data-mp="2uet4xkhl31jc" /> : <BlogQA data-id="element-343" data-mp="33gaq3h9ultew" />}
        </div>
      </div>

    </section>;
}
export function BlogPage({
  onBack
}: BlogPageProps) {
  return <BlogContextProvider data-id="element-344" data-mp="3n3463950d1bi">
      <BlogPageContent onBack={onBack} data-id="element-345" data-mp="3mzq2jxklym2h" />
    </BlogContextProvider>;
}